import type { TrainingReportAttachment } from '../../self-service/surveys/mySurveyTypes';
import type { SurveyDevelopmentNeed, SurveyPlanStatusValue } from '../survey/surveyTypes';

/**
 * One row of Learning & Development › Development Plan — one assessed response, and the plan
 * written from it. `id` is the RESPONSE's id: the plan is carried on it, and Survey's own plan
 * endpoints take it.
 *
 * **No name and no answer.** `personId` is what the Name cell resolves, in one batch; it is ABSENT
 * for an employee above the reader's access level. The answers are `surveys.read`'s.
 */
export interface DevelopmentPlan {
  id: string;
  surveyId: string;
  surveyTitle: string;
  questionnaireTitle: string;
  /** The plan this employee's copy was CREATED from — the register's row it sits under. */
  planId?: string | null;
  planTitle?: string | null;
  personId?: string | null;
  isRestricted: boolean;
  employeeNumber: string;
  /** The employee's CURRENT unit, live from their primary post. */
  orgUnitName?: string | null;
  assessedAt: string;
  assessedByName?: string | null;
  assessmentRemarks?: string | null;
  /** How many live development needs the plan identifies. */
  needCount: number;
  /** When the plan was first written. Absent until then. */
  developmentPlanAt?: string | null;
  developmentPlanByName?: string | null;
  /** Where the plan stands with its approver — `SURVEY_PLAN_STATUS`, numeric on the wire. */
  developmentPlanStatus: SurveyPlanStatusValue;
  /** Whether approval sends the plan to the employee on its own — the plan form's tick. */
  autoSend?: boolean;
  /** When the approved plan was sent to the employee. Absent until then — approved is not sent. */
  sentAt?: string | null;
  /** Who sent it. Absent when approval sent it automatically. */
  sentByName?: string | null;
  /** When the EMPLOYEE accepted or declined the approved plan. */
  employeeDecidedAt?: string | null;
  /** Why the employee declined — what the revision answers. */
  declineReason?: string | null;
  /** The training the employee started on the accepted plan — absent until they record one. */
  trainingTitle?: string | null;
  /** Calendar dates, `YYYY-MM-DD` — `formatDateOnly`, never the instant path. */
  trainingStartDate?: string | null;
  trainingEndDate?: string | null;
  /** When the employee first recorded it — an instant. */
  trainingRecordedAt?: string | null;
  /** Why the employee last corrected the training. Absent while it was never edited. */
  trainingRemarks?: string | null;
  /** When the employee marked the training completed — an instant. Absent until then. */
  trainingCompletedAt?: string | null;
  trainingCompletionRemarks?: string | null;
  /** When the employee first submitted their report on the completed training. Absent until then. */
  trainingReportAt?: string | null;
  /** When the training's impact was first assessed. Absent until then. */
  impactAssessmentAt?: string | null;
  impactAssessmentByName?: string | null;
  /** What Learning & Development concluded about the training's impact. */
  impactAssessmentRemarks?: string | null;
  /** The RESPONSE's live-or-deleted state. */
  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** One plan as its profile reads it. 403 for an employee above the reader's access level. */
export interface DevelopmentPlanDetail {
  summary: DevelopmentPlan;
  surveyIsActive: boolean;
  /** SANITISED markup — rendered only through `<RichTextDocument>`. */
  developmentPlan?: string | null;
  developmentNeeds: SurveyDevelopmentNeed[];
  /** The employee's training report — SANITISED markup, rendered only through `<RichTextDocument>`. */
  trainingReport?: string | null;
  /** The files attached to the report, in order. Absent or empty while it carries none. */
  trainingReportAttachments?: TrainingReportAttachment[];
  /** The plan's approval, from the engine. Absent until submitted, and again once back to Draft. */
  approval?: DevelopmentPlanApproval | null;
}

/** When the plan went to its approver, and when and by whom it was decided. The reason stays under Approvals. */
export interface DevelopmentPlanApproval {
  /** The engine's request — what Review's decision is made against. */
  requestId: string;
  /** Whether the request is waiting on THIS reader's approval — the engine's answer, per caller. */
  canDecide: boolean;
  submittedAt: string;
  decidedAt?: string | null;
  decidedByName?: string | null;
}

/** A written plan's status in words — the server's `planStatus` facet labels, word for word. */
const PLAN_STATUS_LABELS: Record<SurveyPlanStatusValue, string> = {
  0: 'Draft',
  1: 'For approval',
  2: 'Approved',
  3: 'Rejected',
  // The employee's answer to an approved plan.
  4: 'Accepted',
  5: 'Declined',
};

/**
 * Where a WRITTEN plan stands — or null. A plan never written has NO status (product call,
 * 12 Oct 2026): it sits under All, and the cell and the pill that would name one stay empty.
 */
export function planStatusLabel(
  plan: Pick<DevelopmentPlan, 'developmentPlanAt' | 'developmentPlanStatus'>,
): string | null {
  return plan.developmentPlanAt == null ? null : PLAN_STATUS_LABELS[plan.developmentPlanStatus];
}
